/**
 * Storage backend for generated store persistence.
 *
 * Persisted query caches are a convenience: losing one costs a refetch, while a
 * QuotaExceededError thrown from inside a store write aborts whatever action
 * triggered it. localStorage is a ~5MB budget shared by every store, and a
 * single wide list response (e.g. a year of bids) can exhaust it. So writes
 * above the cap, and any storage failure, are dropped (and the stale entry for
 * that key is removed) instead of being surfaced to the caller.
 */

/**
 * Maximum persisted value size, measured in UTF-16 code units (`string.length`),
 * not bytes. Values longer than this are not written.
 */
export const MAX_PERSISTED_CHARS = 1_500_000

/**
 * Wrap a Storage so that no method throws and oversized values are not written.
 * `getStorage` is resolved on every call so an unavailable storage (private
 * mode, SSR, blocked site data) degrades to a no-op instead of throwing.
 */
export function createBoundedStorage(
  getStorage: () => Storage = () => localStorage,
  maxChars: number = MAX_PERSISTED_CHARS
): Storage {
  return {
    get length() {
      try {
        return getStorage().length
      } catch {
        return 0
      }
    },
    key(i: number) {
      try {
        return getStorage().key(i)
      } catch {
        return null
      }
    },
    getItem(k: string) {
      try {
        return getStorage().getItem(k)
      } catch {
        return null
      }
    },
    setItem(k: string, v: string) {
      try {
        if (v.length > maxChars) {
          getStorage().removeItem(k)
          return
        }
        getStorage().setItem(k, v)
      } catch {
        // Quota exceeded or storage unavailable: drop the cache, keep the app.
        try {
          getStorage().removeItem(k)
        } catch {
          /* ignore */
        }
      }
    },
    removeItem(k: string) {
      try {
        getStorage().removeItem(k)
      } catch {
        /* ignore */
      }
    },
    clear() {
      try {
        getStorage().clear()
      } catch {
        /* ignore */
      }
    },
  }
}

/** Bounded, non-throwing localStorage used by every generated store. */
export const boundedLocalStorage: Storage = createBoundedStorage()
